import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Bot, Clock, Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../types/chat';

interface StreamingMessageProps {
  message: Message;
  isStreaming?: boolean;
  streamingText?: string;
}

const StreamingMessage: React.FC<StreamingMessageProps> = ({
  message,
  isStreaming = false,
  streamingText = ''
}) => {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(false);
  // Handle streaming text updates
  useEffect(() => {
    if (isStreaming && streamingText) {
      setDisplayText(streamingText);
      setShowCursor(true);
    } else if (!isStreaming) {
      setDisplayText(message.content);
      setShowCursor(false);
    }
  }, [isStreaming, streamingText, message.content]);

  const isUser = message.role === 'user';
  const IconComponent = isUser ? User : Bot;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.3,
        ease: "easeOut"
      }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}
    >
      {/* Avatar */}
      <div className={`
        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
        ${isUser 
          ? 'bg-primary-500 text-white' 
          : 'bg-gradient-to-br from-purple-500 to-blue-500 text-white'
        }
      `}>
        <IconComponent size={16} />
      </div>

      {/* Message Content */}
      <div className={`
        flex flex-col max-w-[80%] 
        ${isUser ? 'items-end' : 'items-start'}
      `}>
        {/* Message Bubble */}
        <div className={`
          relative px-4 py-3 rounded-2xl shadow-sm border
          ${isUser
            ? 'bg-primary-500 text-white rounded-br-md'
            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 rounded-bl-md'
          }
          ${isStreaming ? 'streaming-message' : ''}
        `}>
          {/* Message Text */}
          <div className="text-sm leading-relaxed">
            {isUser ? (
              // User messages as plain text
              <div className="whitespace-pre-wrap">{displayText}</div>
            ) : (
              // Bot messages with markdown rendering
              <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-code:text-purple-600 dark:prose-code:text-purple-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-ul:text-gray-700 dark:prose-ul:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom components for better styling
                    h1: ({children}) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                    h2: ({children}) => <h2 className="text-base font-semibold mb-2">{children}</h2>,
                    h3: ({children}) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
                    p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                    ul: ({children}) => <ul className="mb-2 pl-4 space-y-1">{children}</ul>,
                    ol: ({children}) => <ol className="mb-2 pl-4 space-y-1">{children}</ol>,
                    li: ({children}) => <li className="text-sm">{children}</li>,
                    code: ({inline, children}: any) => 
                      inline ? (
                        <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono text-purple-600 dark:text-purple-400">
                          {children}
                        </code>
                      ) : (
                        <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs font-mono overflow-x-auto">
                          <code>{children}</code>
                        </pre>
                      ),
                    strong: ({children}) => <strong className="font-semibold text-gray-900 dark:text-gray-100">{children}</strong>,
                    em: ({children}) => <em className="italic">{children}</em>,
                  }}
                >
                  {displayText}
                </ReactMarkdown>
              </div>
            )}
            {/* Typing Cursor */}
            {showCursor && (
              <motion.span
                animate={{ opacity: [1, 0, 1] }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="inline-block w-0.5 h-4 bg-current ml-1 align-middle"
              />
            )}
          </div>

          {/* Streaming Indicator */}
          {isStreaming && (
            <div className="flex items-center gap-1 mt-2 text-xs opacity-70">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <Zap size={12} />
              </motion.div>
              <span>Thinking...</span>
            </div>
          )}
        </div>

        {/* Message Metadata */}
        {!isStreaming && message.metadata && (
          <div className={`
            flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400
            ${isUser ? 'flex-row-reverse' : 'flex-row'}
          `}>
            <Clock size={10} />
            <span>{message.metadata.responseTime}ms</span>
            {message.metadata.tokensUsed > 0 && (
              <>
                <span>•</span>
                <span>{message.metadata.tokensUsed} tokens</span>
              </>
            )}
          </div>
        )}

        {/* Timestamp */}
        <div className={`
          text-xs text-gray-400 dark:text-gray-500 mt-1
          ${isUser ? 'text-right' : 'text-left'}
        `}>
          {new Date(message.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>

    </motion.div>
  );
};

export default StreamingMessage;